import { AnimatePresence, motion } from 'framer-motion';
import { FC, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { DataRoomStatus, SpaceKind } from 'shared';
import { twMerge } from 'tailwind-merge';

import { BreadcrumbTrail } from '../../../../components/breadcrumb/index';
import { CrossfadePanel } from '../../../../components/motion/index';
import { useLocale } from '../../../../hooks/index';
import { useDataRoom, usePatchDataRoom } from '../../../../hooks/space/index';
import { useSelectedCompany } from '../../../../hooks/use-selected-company.hook';
import { PageRoute } from '../../../../types/page.types';
import { AccessRole } from '../../../../types/stakeholder.types';
import { SPACE_UPLOAD_ACCEPT } from '../../constants/space-upload.const';
import { DataRoomModalType } from '../../enums/data-room-modal-type.enum';
import { useRecipientDataRoom } from '../../hooks/use-recipient-data-room.hook';
import { useUploadSpaceFiles } from '../../hooks/use-upload-space-files.hook';
import { useWorkspaceBrowser } from '../../hooks/use-workspace-browser.hook';
import { DataRoomModalStore } from '../../store/data-room-modal.store';
import { WorkspaceBrowserViewProps } from '../../types/data-room.types';
import {
  BrowserContentState,
  browserContentState,
  browserTrail,
} from '../../utils/space-browser.util';
import { VAULT_UPLOAD_ACCEPT } from '../../vault/constants/vault-upload.const';
import { useUploadVaultFiles } from '../../vault/hooks/use-upload-vault-files.hook';
import { BrowserTablePlaceholder } from '../components/workspaces-loading-placeholders';
import { WorkspaceBrowserDropOverlay } from './workspace-browser-drop-overlay';
import { WorkspaceBrowserEmpty } from './workspace-browser-empty';
import { WorkspaceBrowserError } from './workspace-browser-error';
import { WorkspaceBrowserHeader } from './workspace-browser-header';
import { WorkspaceBrowserNoMatches } from './workspace-browser-no-matches';
import { WorkspaceDataRoomStats } from './workspace-data-room-stats';
import { WorkspaceFilesTable } from './workspace-files-table';
import { WorkspaceInactiveRoomBanner } from './workspace-inactive-room-banner';

const ROOT_PANEL_KEY = 'root';
const SEARCH_PANEL_KEY = 'search';

export const WorkspaceBrowserDesktop: FC<WorkspaceBrowserViewProps> = ({
  isRecipientView,
  selection,
}) => {
  const { breadcrumbs, folderId, space } = selection;
  const { messagesLocale } = useLocale();
  const openModal = DataRoomModalStore(({ openModal }) => openModal);
  // The page hides write controls from an auditor (`data-permission-required`); a drop is not one.
  const { selectedCompany } = useSelectedCompany();
  const isAuditor = selectedCompany?.stakeholder?.accessRole === AccessRole.AUDITOR;
  const { archive, contents, navigation, permissions, rowActions, search, sort } =
    useWorkspaceBrowser({ isRecipientView, selection });
  const {
    files,
    folders,
    isError,
    isPending,
    isSearching,
    pagination,
    retry,
    treeSpace,
    vaultUpload,
  } = contents;
  const { canCreateFolder, canManage, canWrite, isReadOnly } = permissions;
  const isVault = space.kind === SpaceKind.VAULT;
  const isDataRoom = space.kind === SpaceKind.DATA_ROOM;
  // Only a data room has a stats strip and the setting that hides it — and only its owner. The
  // route behind this is the owner's `/data-rooms/:id`, which refuses a recipient by design, so
  // asking for it in the recipient view is a 403 and a strip they must not see anyway.
  const { dataRoom } = useDataRoom(isDataRoom && !isRecipientView ? space.id : undefined);
  const { dataRoom: sharedRoom } = useRecipientDataRoom(
    isDataRoom && isRecipientView ? space.id : undefined,
  );
  const { isPending: isReactivating, patchDataRoom } = usePatchDataRoom();
  const [dismissedBannerRoomId, setDismissedBannerRoomId] = useState<null | string>(null);
  const showsInactiveBanner =
    isDataRoom && canManage && isReadOnly && dismissedBannerRoomId !== space.id;
  const navigate = useNavigate();

  const {
    inFlight: spaceInFlight,
    isUploading: isUploadingToSpace,
    uploadFiles,
  } = useUploadSpaceFiles();
  const {
    inFlight: vaultInFlight,
    isUploading: isUploadingToVault,
    uploadFiles: uploadVaultFiles,
  } = useUploadVaultFiles();
  const isUploading = isUploadingToSpace || isUploadingToVault;
  // A Vault folder takes files where the Vault allows them, through the Vault's own upload; the
  // tree writes stay closed to it.
  const canUpload = isVault ? vaultUpload.canUpload : canWrite;

  // Placeholder rows only in the table of the folder the files are going into, never in a search.
  const inFlight = isVault ? vaultInFlight : spaceInFlight?.spaceId === space.id && spaceInFlight;
  const uploadingCount =
    inFlight && inFlight.folderId === folderId && !isSearching ? inFlight.count : 0;
  // A folder with files on their way shows the table, with their rows, rather than the empty state.
  const isEmpty = !isPending && folders.length === 0 && files.length === 0 && uploadingCount === 0;
  const contentState = browserContentState({ isEmpty, isError, isPending });
  const panelKey = `${isSearching ? SEARCH_PANEL_KEY : (folderId ?? ROOT_PANEL_KEY)}:${contentState}`;
  const currentFolderName = breadcrumbs[breadcrumbs.length - 1]?.name ?? space.name;

  // A file the picker filter let through or a drop it rejected goes through the same checks, so
  // an unsupported file still gets its reason rather than vanishing.
  const onDropFiles = (accepted: File[], rejected: FileRejection[]) => {
    const dropped = [...accepted, ...rejected.map(({ file }) => file)];

    if (isVault) {
      if (!folderId || !vaultUpload.canUpload) return;

      uploadVaultFiles({ existingNames: vaultUpload.existingNames, files: dropped, folderId });

      return;
    }

    if (!treeSpace) return;

    uploadFiles({ files: dropped, folderId, space: treeSpace });
  };

  // No click on the area itself: the toolbar and the empty state open the picker. One upload at a
  // time, so the placeholder rows stand for one drop.
  const {
    getInputProps,
    getRootProps,
    isDragActive,
    open: openFilePicker,
  } = useDropzone({
    accept: isVault ? VAULT_UPLOAD_ACCEPT : SPACE_UPLOAD_ACCEPT,
    disabled: !canUpload || isUploading || isAuditor,
    noClick: true,
    noKeyboard: true,
    onDrop: onDropFiles,
  });

  const openCreateFolder = () => openModal({ type: DataRoomModalType.CREATE_FOLDER });

  // 20.2.4.1 §2 makes the toolbar the way in; the card menu on Home is the other one, and both
  // hand the modal the room through `metadata`, which is where it reads the id from.
  const openManageAccess = () =>
    openModal({ metadata: { spaceId: space.id }, type: DataRoomModalType.MANAGE_ACCESS });

  // The button is not rendered for the Vault, which has no settings.
  const openSettings = () =>
    openModal({
      metadata: { spaceId: space.id },
      type: isDataRoom
        ? DataRoomModalType.DATA_ROOM_SETTINGS
        : DataRoomModalType.WORKSPACE_SETTINGS,
    });

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col border-t border-gray-100">
      <WorkspaceBrowserHeader
        canUploadToVault={vaultUpload.canUpload}
        description={isRecipientView ? sharedRoom?.description : dataRoom?.description}
        isDownloadingArchive={archive.isPacking}
        isUploading={isUploading}
        onCreateFolder={openCreateFolder}
        onDownloadArchive={archive.download}
        onManageAccess={isAuditor ? undefined : openManageAccess}
        onSearch={search.onSearch}
        onSettings={openSettings}
        onUpload={openFilePicker}
        permissions={permissions}
        searchKey={search.searchKey}
        space={space}
      />
      {showsInactiveBanner && (
        <div className="px-4 pt-4">
          <WorkspaceInactiveRoomBanner
            canReactivate={!isAuditor}
            isReactivating={isReactivating}
            onDismiss={() => setDismissedBannerRoomId(space.id)}
            onReactivate={() => patchDataRoom({ id: space.id, status: DataRoomStatus.ACTIVE })}
          />
        </div>
      )}
      {/* The root-view analytics widget (20.2.4 §5), and what the Show in-folder stats setting
          turns off. Owner-side only: it reports who visited and links to the Activity tab. At the
          root alone — inside a folder the header above is about the folder, and the strip would
          be reporting something else.

          A room with no files in it has nothing to report, and six zeroes over an empty room say
          less than the empty state below them does. The count comes with the room rather than
          from the open folder, so the strip does not appear and then think better of it while
          the contents load. */}
      {isDataRoom &&
        dataRoom?.settings.showInFolderStats &&
        dataRoom.metrics.fileCount > 0 &&
        !breadcrumbs.length &&
        !isSearching && (
          <div className="px-4 pt-4">
            <WorkspaceDataRoomStats
              metrics={dataRoom.metrics}
              onOpenFile={navigation.openNode}
              onViewActivity={() =>
                navigate(PageRoute.DATA_ROOM_ACTIVITY_ROOM.replace(':dataRoomId', space.id))
              }
            />
          </div>
        )}
      {/* Grows in from zero height, so leaving the root does not push the table down in one jump. */}
      <AnimatePresence initial={false}>
        {breadcrumbs.length > 0 && !isSearching && (
          <motion.div
            animate={{ height: 'auto', opacity: 1 }}
            className="overflow-hidden"
            exit={{ height: 0, opacity: 0 }}
            initial={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <div className="border-b border-gray-100 px-4 py-4">
              <BreadcrumbTrail
                ellipsisLabel={messagesLocale['SHOW_HIDDEN_FOLDERS']}
                items={browserTrail({ breadcrumbs, rootLabel: space.name, spaceId: space.id })}
                label={messagesLocale['BREADCRUMB']}
                onSelect={navigation.openNode}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* The drop target does not scroll, so its frame and message stay in view over a long table. */}
      <div {...getRootProps({ className: 'relative flex min-h-0 flex-1 flex-col' })}>
        <input {...getInputProps()} />
        <div
          className={twMerge(
            'flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-4 transition-opacity duration-150',
            isDragActive && 'opacity-20',
          )}
        >
          {/* Opacity only: a transform shows the table's row dividers as seams. */}
          <AnimatePresence initial={false} mode="wait">
            <CrossfadePanel
              className="flex flex-1 flex-col"
              key={panelKey}
              panelKey={panelKey}
              translate={false}
            >
              {contentState === BrowserContentState.LOADING && <BrowserTablePlaceholder />}
              {contentState === BrowserContentState.ERROR && (
                <WorkspaceBrowserError onRetry={retry} />
              )}
              {contentState === BrowserContentState.EMPTY && isSearching && (
                <WorkspaceBrowserNoMatches
                  className="m-auto"
                  isWorkspace={space.kind === SpaceKind.WORKSPACE}
                />
              )}
              {contentState === BrowserContentState.EMPTY && !isSearching && (
                <WorkspaceBrowserEmpty
                  isUploading={isUploading}
                  onCreateFolder={canCreateFolder ? openCreateFolder : undefined}
                  onUpload={canUpload ? openFilePicker : undefined}
                />
              )}
              {contentState === BrowserContentState.READY && (
                <WorkspaceFilesTable
                  actions={rowActions}
                  files={files}
                  folders={folders}
                  onOpenFolder={navigation.openNode}
                  onSortChange={sort.setSort}
                  pagination={pagination}
                  sort={sort.active}
                  sortableBy={sort.sortableBy}
                  uploadingCount={uploadingCount}
                />
              )}
            </CrossfadePanel>
          </AnimatePresence>
        </div>
        <AnimatePresence>
          {isDragActive && <WorkspaceBrowserDropOverlay folderName={currentFolderName} />}
        </AnimatePresence>
      </div>
    </div>
  );
};
